/**
 * BatchQuestionDrafter — Iter 414 "draft-then-edit" loop for teachers.
 *
 * Opens as a sibling dialog to QuestionsDialog. Teacher types topics (one per
 * line, up to 20) and picks type/difficulty/subject. Hits
 * /api/school/portal/teacher/questions/ai-suggest-batch which fans the calls
 * out concurrently. Response is a checkbox list of drafts; teacher can:
 *   • toggle each keep/discard,
 *   • edit the question text / correct answer inline,
 *   • click "Save selected to exam" which loops the existing
 *     POST /exams/{id}/questions endpoint (one call per selected draft).
 *
 * When done, the parent QuestionsDialog refetches the list.
 */
import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkles, Loader2, CheckCircle2, X, Wand2 } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const _token = () => {
  try { const p = sessionStorage.getItem('school_preview_token'); if (p) return p; } catch { /* ignore */ }
  return getToken();
};

const QUESTION_TYPES = [
  { id: 'mcq_single', label: 'MCQ · Single answer' },
  { id: 'mcq_multi', label: 'MCQ · Multiple answers' },
  { id: 'true_false', label: 'True / False' },
  { id: 'short_answer', label: 'Short answer' },
  { id: 'long_essay', label: 'Long essay' },
  { id: 'fill_blank', label: 'Fill in the blank' },
  { id: 'matching', label: 'Matching' },
];

const BatchQuestionDrafter = ({ examId, subjects, open, onOpenChange, onSaved }) => {
  const [topicsText, setTopicsText] = useState('');
  const [questionType, setQuestionType] = useState('mcq_single');
  const [difficulty, setDifficulty] = useState('medium');
  const [subjectId, setSubjectId] = useState(subjects?.[0]?.subject_id || subjects?.[0]?.id || '');
  const [isScripture, setIsScripture] = useState(false);
  const [styleHint, setStyleHint] = useState('');
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);  // {draft_id, ..., _keep}
  const [failed, setFailed] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());

  const topicList = useMemo(
    () => topicsText.split('\n').map((t) => t.trim()).filter(Boolean),
    [topicsText]
  );

  const reset = () => {
    setTopicsText('');
    setDrafts([]);
    setFailed([]);
    setSavedIds(new Set());
    setStyleHint('');
  };

  const closeAll = (open) => {
    if (!open) reset();
    onOpenChange(open);
  };

  const runBatch = async () => {
    if (topicList.length === 0) {
      toast.error('Add at least one topic (one per line).');
      return;
    }
    if (topicList.length > 20) {
      toast.error('Batch draft is limited to 20 topics — split into smaller runs.');
      return;
    }
    setBusy(true);
    setDrafts([]);
    setFailed([]);
    setSavedIds(new Set());
    try {
      const res = await fetch(
        `${API_URL}/api/school/portal/teacher/questions/ai-suggest-batch`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${_token()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            topics: topicList,
            question_type: questionType,
            subject_id: subjectId || undefined,
            difficulty,
            exam_id: examId,
            is_scripture: isScripture,
            style_hint: styleHint || undefined,
          }),
        }
      );
      if (!res.ok) throw new Error((await res.json()).detail || `Failed (${res.status})`);
      const data = await res.json();
      const marked = (data.drafts || []).map((d) => ({ ...d, _keep: true }));
      setDrafts(marked);
      setFailed(data.failed || []);
      const okMsg = `${data.succeeded}/${data.requested} drafts ready`;
      if (data.failed?.length) toast.warning(`${okMsg} — ${data.failed.length} failed, see below`);
      else toast.success(okMsg);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const setDraft = (idx, patch) => {
    setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  };
  const setDraftOption = (idx, oIdx, value) => {
    setDrafts((prev) => prev.map((d, i) => {
      if (i !== idx) return d;
      const opts = [...(d.options || [])];
      opts[oIdx] = value;
      return { ...d, options: opts };
    }));
  };

  const toKeep = drafts.filter((d) => d._keep && !savedIds.has(d.draft_id));

  const saveSelected = async () => {
    if (toKeep.length === 0) {
      toast.error('Select at least one draft to save.');
      return;
    }
    setSaving(true);
    const savedNow = new Set(savedIds);
    let saved = 0;
    let failed = 0;
    for (const draft of toKeep) {
      try {
        const payload = {
          subject_id: draft.subject_id || subjectId,
          question_type: draft.question_type,
          question_text: draft.question_text,
          options: draft.options || [],
          correct_answer: draft.correct_answer,
          correct_answers: draft.correct_answers || [],
          marks: Number(draft.marks || 1),
          difficulty: draft.difficulty || 'medium',
          explanation: draft.explanation || '',
          topic: draft.topic || '',
          is_scripture: !!draft.is_scripture,
          tags: [],
        };
        const res = await fetch(
          `${API_URL}/api/school/portal/teacher/exams/${examId}/questions`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${_token()}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }
        );
        if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
        savedNow.add(draft.draft_id);
        saved += 1;
      } catch (e) {
        failed += 1;
        console.warn('[batch-draft] save failed for topic', draft.topic, e);
      }
    }
    setSavedIds(savedNow);
    setSaving(false);
    if (failed > 0) toast.warning(`Saved ${saved} · ${failed} failed`);
    else toast.success(`Saved ${saved} question(s) to the exam`);
    if (typeof onSaved === 'function') onSaved();
  };

  const allDone = drafts.length > 0 && drafts.every((d) => savedIds.has(d.draft_id));

  return (
    <Dialog open={open} onOpenChange={closeAll}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="batch-drafter-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-violet-800">
            <Wand2 className="h-5 w-5" /> Batch Draft with Claude
            <Badge variant="outline" className="text-xs">Iter 414</Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Input form */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 border-b pb-3">
          <div className="col-span-2 space-y-1">
            <Label className="text-xs">Topics · one per line (max 20)</Label>
            <textarea
              className="w-full text-sm border rounded p-2 min-h-[110px] font-mono"
              placeholder={"Photosynthesis basics\nCell membrane transport\nMitosis vs meiosis\n..."}
              value={topicsText}
              onChange={(e) => setTopicsText(e.target.value)}
              data-testid="batch-topics"
            />
            <p className="text-[11px] text-slate-500">
              {topicList.length} topic(s) queued · ~{Math.max(3, Math.ceil(topicList.length / 5) * 4)}s expected
            </p>
          </div>
          <div className="space-y-2">
            <div>
              <Label className="text-xs">Subject</Label>
              <Select value={subjectId} onValueChange={setSubjectId}>
                <SelectTrigger className="h-8" data-testid="batch-subject"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(subjects || []).map((s) => (
                    <SelectItem key={s.subject_id || s.id} value={s.subject_id || s.id}>
                      {s.subject_name || s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Question type</Label>
              <Select value={questionType} onValueChange={setQuestionType}>
                <SelectTrigger className="h-8" data-testid="batch-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {QUESTION_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Difficulty</Label>
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger className="h-8" data-testid="batch-difficulty"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={isScripture}
                onChange={(e) => setIsScripture(e.target.checked)}
                data-testid="batch-scripture"
              /> Scripture-based
            </label>
            <div>
              <Label className="text-xs">Style hint (optional)</Label>
              <Input
                className="h-8 text-xs"
                value={styleHint}
                onChange={(e) => setStyleHint(e.target.value)}
                placeholder="e.g. Grade 8 level, real-world context"
                data-testid="batch-style"
              />
            </div>
            <Button
              className="w-full mt-1 bg-violet-600 hover:bg-violet-700"
              onClick={runBatch}
              disabled={busy || topicList.length === 0}
              data-testid="batch-generate-btn"
            >
              {busy
                ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Drafting {topicList.length}…</>
                : <><Sparkles className="h-4 w-4 mr-1" /> Draft {topicList.length || 'N'} questions</>}
            </Button>
          </div>
        </div>

        {/* Draft results */}
        <div className="flex-1 overflow-y-auto space-y-2 pt-3 pr-1" data-testid="batch-results">
          {drafts.length === 0 && failed.length === 0 && !busy && (
            <div className="text-center text-slate-500 text-sm p-6 border border-dashed rounded">
              Add topics on the left, then click <b>Draft N questions</b>.
              Drafts appear here for review.
            </div>
          )}
          {failed.length > 0 && (
            <div className="border border-red-200 bg-red-50 rounded p-2 text-xs" data-testid="batch-failed">
              <p className="font-semibold text-red-800 mb-1">{failed.length} topic(s) failed:</p>
              <ul className="list-disc list-inside text-red-700">
                {failed.map((f, i) => (
                  <li key={i}><b>{f.topic}</b> — {f.error}</li>
                ))}
              </ul>
            </div>
          )}
          {drafts.map((d, idx) => {
            const isSaved = savedIds.has(d.draft_id);
            return (
              <div
                key={d.draft_id}
                className={`border rounded p-2 text-sm ${isSaved ? 'bg-emerald-50 border-emerald-200' : d._keep ? 'bg-white' : 'bg-slate-50 opacity-70'}`}
                data-testid={`draft-row-${idx}`}
              >
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={d._keep}
                    disabled={isSaved}
                    onChange={(e) => setDraft(idx, { _keep: e.target.checked })}
                    data-testid={`draft-keep-${idx}`}
                  />
                  <div className="flex-1 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{d.topic}</Badge>
                      <Badge variant="outline" className="text-[10px]">{d.question_type}</Badge>
                      {isSaved && (
                        <Badge className="bg-emerald-600 text-[10px]">
                          <CheckCircle2 className="h-3 w-3 mr-0.5" /> Saved
                        </Badge>
                      )}
                    </div>
                    <textarea
                      className="w-full text-sm border rounded p-1.5 min-h-[46px]"
                      value={d.question_text}
                      disabled={isSaved}
                      onChange={(e) => setDraft(idx, { question_text: e.target.value })}
                      data-testid={`draft-text-${idx}`}
                    />
                    {(d.options || []).length > 0 && (
                      <div className="grid grid-cols-2 gap-1">
                        {d.options.map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-1">
                            <input
                              type={d.question_type === 'mcq_multi' ? 'checkbox' : 'radio'}
                              name={`ans-${d.draft_id}`}
                              disabled={isSaved}
                              checked={
                                d.question_type === 'mcq_multi'
                                  ? (d.correct_answers || []).includes(opt)
                                  : d.correct_answer === opt
                              }
                              onChange={() => {
                                if (d.question_type === 'mcq_multi') {
                                  const set = new Set(d.correct_answers || []);
                                  if (set.has(opt)) set.delete(opt);
                                  else set.add(opt);
                                  setDraft(idx, { correct_answers: [...set] });
                                } else {
                                  setDraft(idx, { correct_answer: opt });
                                }
                              }}
                            />
                            <input
                              className="flex-1 border rounded px-1.5 py-0.5 text-xs"
                              value={opt}
                              disabled={isSaved}
                              onChange={(e) => setDraftOption(idx, oi, e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    {d.explanation && (
                      <p className="text-[11px] text-slate-600 italic border-l-2 border-slate-200 pl-2">
                        {d.explanation}
                      </p>
                    )}
                  </div>
                  {!isSaved && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => setDrafts((prev) => prev.filter((_, i) => i !== idx))}
                      data-testid={`draft-discard-${idx}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="border-t pt-3">
          <div className="flex items-center justify-between w-full">
            <p className="text-xs text-slate-500">
              {drafts.length > 0 && !allDone && `${toKeep.length} selected · ${savedIds.size} saved`}
              {allDone && `All ${savedIds.size} drafts saved.`}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => closeAll(false)}>Close</Button>
              <Button
                onClick={saveSelected}
                disabled={saving || busy || toKeep.length === 0}
                data-testid="batch-save-selected"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                Save {toKeep.length || ''} to exam
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BatchQuestionDrafter;
